import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { UnitOfMeasure } from '../../enums';
import { AllowedShippingMethods } from '../../types';
import type {
	ItemEquipmentAction,
	ItemLevel,
	VariantDisplayMode,
} from '../types';
import type {
	ItemErpContentModel,
	ItemFieldSourcesModel,
} from '../../utils/item-field-sources';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientItemEntity } from './client-item.entity';
import { CompanyEntity } from './company.entity';
import { CompetitivePriceEntity } from './competitive-price.entity';
import { InvoiceDetailEntity } from './invoice-detail.entity';
import { ItemCategoryEntity } from './item-category.entity';
import { ItemCompanyEntity } from './item-company.entity';
import { ItemDeaScheduleEntity } from './item-dea-schedule.entity';
import { ItemFileEntity } from './item-file.entity';
import { ItemGroupEntity } from './item-group.entity';
import { ItemImageEntity } from './item-image.entity';
import { ItemLotEntity } from './item-lot.entity';
import { ItemSalesEntity } from './item-sales.entity';
import { ItemWarehouseEntity } from './item-warehouse.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { PriceContractItemEntity } from './price-contract-item.entity';
import { PromoItemEntity } from './promo-item.entity';
import { ShipmentDetailEntity } from './shipment-detail.entity';
import { VendorItemEntity } from './vendor-item.entity';

@Index('category_isActive_index', ['isActive', 'category'], {})
@Index(
	'externalId_fk_company__id_primary',
	['externalId', 'fkCompanyIdPrimary'],
	{ unique: true },
)
@Index('fk_company__id_primary', ['fkCompanyIdPrimary'], {})
@Index(
	'fx_item_fulltext',
	[
		'description',
		'manufacturer',
		'unitOfMeasure',
		'strength',
		'externalId',
		'externalId2',
		'brandEquivalent',
		'weight',
		'upc',
		'category',
		'caseUpc',
		'retailUpc',
		'extraUpc',
		'subcategory',
		'extendedDescription',
		'manufacturerItemId',
	],
	{ fulltext: true },
)
@Index(
	'item_isActive_date_index',
	['isActive', 'createdDate', 'isShortDate', 'externalCreatedDate'],
	{},
)
@Index(
	'item_isActive_deletedUserId_description',
	['description', 'isActive', 'deletedUserId'],
	{},
)
@Index('item_ndc_index', ['ndc'], {})
@Index('idx_item_deleted_active', ['deletedDate', 'isActive'], {})
@Index('idx_item_parent', ['fkItemIdParent'], {})
@Index('idx_item_itemLevel', ['itemLevel'], {})
@Index('idx_item_gtin', ['gtin'], {})
@Entity('item')
export class ItemEntity<
	TCustomFieldsModel extends { [key: string]: unknown } = {
		[key: string]: unknown;
	},
> extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 64 })
	public externalId2!: string | null;

	@Column('int', {
		name: 'fk_company__id_primary',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyIdPrimary!: number;

	// The parent chain: company item → master item → product (ADR 0004).
	// A `variant` points at its `product`; a sellable item linked to its
	// counterparts points at their `master` item; a `master` may point at a
	// `product`. A product never has a parent, so the chain is at most two deep.
	@Column('int', {
		name: 'fk_item__id_parent',
		nullable: true,
		unsigned: true,
	})
	public fkItemIdParent!: number | null;

	@Column('varchar', {
		name: 'itemLevel',
		length: 16,
		default: 'standalone',
	})
	public itemLevel!: ItemLevel;

	// GTIN-13/14 does not fit the legacy char(12) upc columns, so it gets its
	// own column rather than overloading extraUpc.
	@Column('varchar', { name: 'gtin', nullable: true, length: 14 })
	public gtin!: string | null;

	// Meaningful only on `product` rows. NULL means 'grouped' by app logic.
	@Column('varchar', {
		name: 'variantDisplayMode',
		nullable: true,
		length: 16,
	})
	public variantDisplayMode!: VariantDisplayMode | null;

	// Where each content field gets its value (PD-16509). Key = content field,
	// `customFields.<key>`, `images` or `files`; value = 'workd' (edited in
	// Workd) or 'inherit' (copied from the parent). A missing key means the
	// item's ERP owns the field. NULL for every item nobody has touched.
	// mover-integration reads it to decide which live columns an ERP sync may
	// write. See libs/shared/src/utils/item-field-sources.ts.
	@Column('json', { name: 'fieldSources', nullable: true })
	public fieldSources!: ItemFieldSourcesModel | null;

	// The ERP shadow: the latest value the ERP supplied for every content field,
	// written by every ERP import whether or not the live column takes it. Lets
	// unlink and "Sync from ERP" restore ERP values without waiting for the ERP
	// record to change.
	@Column('json', { name: 'erpContent', nullable: true })
	public erpContent!: ItemErpContentModel | null;

	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	// Pack or specification text, e.g. "6*5LB" (HF maps Fishbowl's
	// Specifications custom field here).
	@Column('text', { name: 'specifications', nullable: true })
	public specifications!: string | null;

	@Column('longtext', { name: 'extendedDescription', nullable: true })
	public extendedDescription!: string | null;

	@Column('varchar', { name: 'manufacturer', nullable: true, length: 255 })
	public manufacturer!: string | null;

	@Column('varchar', {
		name: 'manufacturerItemId',
		nullable: true,
		length: 255,
	})
	public manufacturerItemId!: string | null;

	@Column('varchar', { name: 'brandEquivalent', nullable: true, length: 255 })
	public brandEquivalent!: string | null;

	@Column('varchar', { name: 'category', nullable: true, length: 64 })
	public category!: string | null;

	@Column('varchar', { name: 'subcategory', nullable: true, length: 64 })
	public subcategory!: string | null;

	@Column('decimal', {
		name: 'sizeCount',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public sizeCount!: string | null;

	// Kept as varchar in DB because this column is part of the fx_item_fulltext FULLTEXT index,
	// which does not support enum columns. Enum is enforced at the DTO/application layer.
	@Column('varchar', { name: 'unitOfMeasure', nullable: true, length: 255 })
	public unitOfMeasure!: UnitOfMeasure | null;

	@Column('varchar', { name: 'strength', nullable: true, length: 255 })
	public strength!: string | null;

	@Column('varchar', { name: 'countryOfOrigin', nullable: true, length: 255 })
	public countryOfOrigin!: string | null;

	@Column('varchar', { name: 'family', nullable: true, length: 255 })
	public family!: string | null;

	@Column('varchar', { name: 'storageType', nullable: true, length: 255 })
	public storageType!: string | null;

	@Column('varchar', { name: 'tradeStyle', nullable: true, length: 255 })
	public tradeStyle!: string | null;

	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	// ---------------------------------------------------------------
	// Quick order at dispatch (PD-15005)
	// ---------------------------------------------------------------
	// Whether this item appears as a quantity column in the dispatcher's
	// quick-order grid. Deliberately opt-in: the grid is for the products a
	// truck actually carries (Motor City: two bag sizes), and the flag keeps
	// non-product items like Merchandiser Rental and Pallet Deposit out of it.
	@Column({
		type: 'boolean',
		name: 'quickOrderEligible',
		width: 1,
		default: () => "'0'",
	})
	public quickOrderEligible!: boolean;

	// Left-to-right order of the quick-order grid's item columns. NULL sorts
	// last; ties fall back to item description.
	@Column('int', { name: 'quickOrderSortOrder', nullable: true })
	public quickOrderSortOrder!: number | null;

	@Column('varchar', {
		name: 'ecommerceVisibility',
		nullable: true,
		length: 64,
	})
	public ecommerceVisibility!: string | null;

	@Column('decimal', { name: 'price', nullable: true, precision: 15, scale: 6 })
	public price!: string | null;

	@Column('decimal', {
		name: 'minimumPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public minimumPrice!: string | null;

	@Column('decimal', {
		name: 'previousPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public previousPrice!: string | null;

	@Column('datetime', { name: 'priceModifiedDatetime', nullable: true })
	public priceModifiedDatetime!: Date | null;

	@Column('int', { name: 'quantityOnHand', nullable: true })
	public quantityOnHand!: number | null;

	@Column('int', { name: 'quantityOnOrder', nullable: true })
	public quantityOnOrder!: number | null;

	@Column('int', { name: 'quantityOnBackorder', nullable: true })
	public quantityOnBackorder!: number | null;

	/** Quantity reserved by submitted sales orders but not yet picked. Available qty = quantityOnHand - quantityReserved. */
	@Column('int', { name: 'quantityReserved', nullable: true, default: 0 })
	public quantityReserved!: number | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('date', { name: 'externalCreatedDate', nullable: true })
	public externalCreatedDate!: string | null;

	@Column('char', { name: 'upc', nullable: true, length: 12 })
	public upc!: string | null;

	@Column('char', { name: 'caseUpc', nullable: true, length: 12 })
	public caseUpc!: string | null;

	@Column('char', { name: 'retailUpc', nullable: true, length: 12 })
	public retailUpc!: string | null;

	@Column('varchar', { name: 'extraUpc', nullable: true, length: 64 })
	public extraUpc!: string | null;

	@Column('varchar', { name: 'ndc', nullable: true, length: 32 })
	public ndc!: string | null;

	@Column('varchar', { name: 'standardNdc', nullable: true, length: 255 })
	public standardNdc!: string | null;

	@Column('decimal', {
		name: 'averageWholesalePrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public averageWholesalePrice!: string | null;

	@Column({ type: 'boolean', name: 'isShortDate', width: 1 })
	public isShortDate!: boolean;

	@Column({ type: 'boolean', name: 'isHazmat', width: 1, default: () => "'0'" })
	public isHazmat!: boolean;

	@Column({ type: 'boolean', name: 'isRefrigerated', width: 1, default: () => "'0'" })
	public isRefrigerated!: boolean;

	@Column({ type: 'boolean', name: 'isHeavy', width: 1, default: () => "'0'" })
	public isHeavy!: boolean;

	// ---------------------------------------------------------------
	// Non-inventory items (PD-15021)
	// ---------------------------------------------------------------
	// A fee, deposit, rental or service charge: sold, priced and invoiced
	// exactly like any other item, but never a physical thing on a shelf.
	// When set, the item is excluded from everything that counts, picks,
	// loads or reconciles stock, and InventoryService refuses to record a
	// movement for it. Sellability is deliberately untouched — the point is
	// to remove it from STOCK, not from SALES.
	@Column({
		type: 'boolean',
		name: 'isNonInventory',
		width: 1,
		default: () => "'0'",
	})
	public isNonInventory!: boolean;

	// PD-16114 — what an order line for this item does with placed equipment:
	// `drop_off` places a unit at the stop's address, `pick_up` brings one back
	// to the warehouse, `none` (every existing item) moves nothing. This is how
	// the driver app and the Schedule know a line needs a serial and a photo.
	// NOT NULL with a database default, so SM1's item inserts (which list
	// their columns) are unaffected.
	@Column('varchar', {
		name: 'equipmentAction',
		length: 16,
		default: 'none',
	})
	public equipmentAction!: ItemEquipmentAction;

	@Column('decimal', {
		name: 'caseSize',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public caseSize!: string | null;

	/**
	 * Base units (eaches / bags / bottles — whatever the item's
	 * `unitOfMeasure` is) that fit on one pallet. Matches NetSuite's
	 * "Units Per Pallet" naming. Used by warehouse planning + truck
	 * loading. The "cases per pallet" view is computed at the UI layer
	 * as `unitsPerPallet / caseSize` so a single source of truth is
	 * kept.
	 */
	@Column('decimal', {
		name: 'unitsPerPallet',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public unitsPerPallet!: string | null;

	@Column('varchar', { name: 'weight', nullable: true, length: 64 })
	public weight!: string | null;

	@Column('varchar', { name: 'length', nullable: true, length: 64 })
	public length!: string | null;

	@Column('varchar', { name: 'width', nullable: true, length: 64 })
	public width!: string | null;

	@Column('varchar', { name: 'height', nullable: true, length: 64 })
	public height!: string | null;

	@Column('decimal', {
		name: 'acquisitionCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public acquisitionCost!: string | null;

	@Column('decimal', {
		name: 'sellCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public sellCost!: string | null;

	@Column('decimal', {
		name: 'averageCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public averageCost!: string | null;

	@Column('decimal', {
		name: 'averageNetCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public averageNetCost!: string | null;

	// Free-form purchasing guidance maintained in the tenant's ERP and surfaced
	// to internal sales staff (PD-15444). Unbounded text rather than a varchar
	// because the ERP field it mirrors has no enforced length.
	@Column('text', { name: 'purchasingNotes', nullable: true })
	public purchasingNotes!: string | null;

	@Column('date', { name: 'soonestArrivalDate', nullable: true })
	public soonestArrivalDate!: string | null;

	// The purchase order whose open line supplies `soonestArrivalDate`
	// (PD-15581). Denormalized by the integration's on-order aggregation in the
	// same write as the date, so every surface that shows the date can link it
	// to the PO without re-deriving which line won. Nullable: items with no
	// open PO lines have neither a date nor a PO.
	@Column('int', {
		name: 'fk_purchase_order__id_soonestArrival',
		nullable: true,
		unsigned: true,
	})
	public soonestArrivalPurchaseOrderId!: number | null;

	@Column('date', { name: 'soonestExpirationDate', nullable: true })
	public soonestExpirationDate!: string | null;

	@Column('date', { name: 'lastOutOfStockDate', nullable: true })
	public lastOutOfStockDate!: string | null;

	@Column('date', { name: 'lastRestockedDate', nullable: true })
	public lastRestockedDate!: string | null;

	@Column('int', { name: 'dailyLimit', nullable: true })
	public dailyLimit!: number | null;

	// Max quantity a client can order over a rolling 30-day window (PD-14038).
	// "Monthly" per the NetSuite source field, but the window is last-30-days, not calendar month.
	@Column('int', { name: 'monthlyLimit', nullable: true })
	public monthlyLimit!: number | null;

	@Column('int', { name: 'perOrderLimit', nullable: true })
	public perOrderLimit!: number | null;

	@Column('decimal', {
		name: 'conversionFactor',
		precision: 11,
		scale: 4,
		default: () => "'1.0000'",
	})
	public conversionFactor!: string;

	@Column('decimal', {
		name: 'invoiceFrequency',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public invoiceFrequency!: string | null;

	@Column('int', { name: 'soldUnitsAggregate', nullable: true })
	public soldUnitsAggregate!: number | null;

	@Column('decimal', {
		name: 'revenueAggregate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueAggregate!: string | null;

	@Column('int', { name: 'uniqueClientsAggregate', nullable: true })
	public uniqueClientsAggregate!: number | null;

	@Column('int', { name: 'uniqueInvoicesAggregate', nullable: true })
	public uniqueInvoicesAggregate!: number | null;

	@Column('int', { name: 'soldUnitsMtd', nullable: true })
	public soldUnitsMtd!: number | null;

	@Column('int', { name: 'soldUnitsLastMonth', nullable: true })
	public soldUnitsLastMonth!: number | null;

	@Column('int', { name: 'soldUnitsTwoMonthsAgo', nullable: true })
	public soldUnitsTwoMonthsAgo!: number | null;

	@Column('decimal', {
		name: 'revenueMtd',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueMtd!: string | null;

	@Column('decimal', {
		name: 'revenueLastMonth',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueLastMonth!: string | null;

	@Column('decimal', {
		name: 'revenueTwoMonthsAgo',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueTwoMonthsAgo!: string | null;

	@Column('int', { name: 'uniqueClientsMtd', nullable: true })
	public uniqueClientsMtd!: number | null;

	@Column('int', { name: 'uniqueClientsLastMonth', nullable: true })
	public uniqueClientsLastMonth!: number | null;

	@Column('int', { name: 'uniqueClientsTwoMonthsAgo', nullable: true })
	public uniqueClientsTwoMonthsAgo!: number | null;

	@Column('int', { name: 'uniqueInvoicesMtd', nullable: true })
	public uniqueInvoicesMtd!: number | null;

	@Column('int', { name: 'uniqueInvoicesLastMonth', nullable: true })
	public uniqueInvoicesLastMonth!: number | null;

	@Column('int', { name: 'uniqueInvoicesTwoMonthsAgo', nullable: true })
	public uniqueInvoicesTwoMonthsAgo!: number | null;

	@Column('decimal', {
		name: 'totalGPAggregate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpAggregate!: string | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend90!: string | null;

	@Column('json', {
		name: 'allowedShippingMethods',
		nullable: true,
		comment:
			'JSON structured object indicating what shipping methods are allowed and associated fees for each.',
	})
	public allowedShippingMethods!: AllowedShippingMethods | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment:
			'A JSON string representing a key-value pair object with tenant-specific custom fields.',
	})
	public customFields!: TCustomFieldsModel | null;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkItem,
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkItem)
	public clientItems!: Array<ClientItemEntity>;

	@OneToMany(
		() => CompetitivePriceEntity,
		(competitivePrice) => competitivePrice.fkItem,
	)
	public competitivePrices!: Array<CompetitivePriceEntity>;

	@OneToMany(() => InvoiceDetailEntity, (invoiceDetail) => invoiceDetail.item)
	public invoiceDetails!: Array<InvoiceDetailEntity>;

	@OneToMany(() => ItemFileEntity, (itemFile) => itemFile.fkItem)
	public itemFiles!: Array<ItemFileEntity>;

	@OneToMany(() => ItemCategoryEntity, (itemCategory) => itemCategory.item)
	public itemCategories!: Array<ItemCategoryEntity>;

	@OneToMany(() => ItemCompanyEntity, (itemCompany) => itemCompany.item)
	public itemCompanies!: Array<ItemCompanyEntity>;

	@OneToMany(() => ItemGroupEntity, (itemGroup) => itemGroup.item)
	public itemGroups!: Array<ItemGroupEntity>;

	@OneToMany(() => ItemImageEntity, (itemImage) => itemImage.fkItem)
	public itemImages!: Array<ItemImageEntity>;

	@OneToMany(() => ItemLotEntity, (itemLot) => itemLot.item)
	public itemLots!: Array<ItemLotEntity>;

	@OneToMany(() => ItemSalesEntity, (itemSales) => itemSales.fkItem)
	public itemSales!: Array<ItemSalesEntity>;

	@OneToMany(() => ItemWarehouseEntity, (itemWarehouse) => itemWarehouse.item)
	public itemWarehouses!: Array<ItemWarehouseEntity>;

	@OneToMany(() => OrderDetailEntity, (orderDetail) => orderDetail.item)
	public orderDetails!: Array<OrderDetailEntity>;

	@OneToMany(
		() => PriceContractItemEntity,
		(priceContractItem) => priceContractItem.item,
	)
	public priceContractItems!: Array<PriceContractItemEntity>;

	@OneToMany(() => PromoItemEntity, (promoItem) => promoItem.fkItem)
	public promoItems!: Array<PromoItemEntity>;

	@OneToMany(
		() => ShipmentDetailEntity,
		(shipmentDetail) => shipmentDetail.item,
	)
	public shipmentDetails!: Array<ShipmentDetailEntity>;

	@OneToMany(() => VendorItemEntity, (vendorItem) => vendorItem.item)
	public vendorItems!: Array<VendorItemEntity>;

	@OneToMany(
		() => ItemDeaScheduleEntity,
		(itemDeaSchedule) => itemDeaSchedule.fkItem,
	)
	public itemDeaSchedules!: Array<ItemDeaScheduleEntity>;

	@ManyToOne(() => CompanyEntity, (company) => company.items, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id_primary', referencedColumnName: 'id' }])
	public primaryCompany!: CompanyEntity;
}
